import {
    Ad,
    AdBadge,
    AdContent,
    AdContentDetails,
    AdContentTitle,
    AdImage,
    AdImageFrame,
    AdImageFrameOverlay,
    AdLink,
    AdLogo,
    AdTools,
} from '@finn-no/troika-react-ads';
import {
    ImageFormat,
    LogoImageFormat,
    SquareAutoImageFormat,
} from '@finn-no/troika-react-auto-image-format';
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useTrackInscreen } from '../../hooks';
import Tracking from '../../tracking';
import Overflowmenu from '../Overflowmenu';
import classNames from 'classnames';
import ShippingOptionLabel from './ShippingOptionLabel';

type ExternalImageFormatProps = {
    url: string;
    cdnUrl?: string;
    isAbsolute?: boolean;
} & Pick<
    React.ComponentProps<typeof ImageFormat>,
    'alt' | 'height' | 'sizes' | 'width'
>;
const ExternalImageFormat = ({
    url,
    cdnUrl,
    isAbsolute,
    ...props
}: ExternalImageFormatProps) => (
    <ImageFormat
        src={url}
        ratio="1by1"
        className="bg-milk"
        type={isAbsolute ? undefined : 'logo'}
        centered
        {...props}
    />
);

// @TODO from react-ad-preview
function selectImageComp(type) {
    switch (type) {
        case 'LOGO':
            return LogoImageFormat;
        case 'EXTERNAL':
            return ExternalImageFormat;
        default:
            return SquareAutoImageFormat;
    }
}

function isFavouriteClicked(e, ref) {
    return ref.current.contains(e.target as HTMLElement);
}

type AdProps = {
    ad: import('../../types').Recommendation;
    trackAdClick: (adId: string, trackingUrlClick: string[]) => void;
    imagesCdnUrl: string;
    logo: string;
    tracking: Tracking;
    useOverflow: boolean;
    url?: string;
    product: string;
} & Pick<
    React.ComponentProps<
        typeof import('@finn-no/troika-react-ad-preview').default
    >,
    'rightActions'
>;
const StandardAd = (props: AdProps) => {
    const {
        trackAdClick,
        ad,
        tracking,
        logo,
        rightActions,
        imagesCdnUrl,
        useOverflow,
        url,
        product,
    } = props;

    const [badgeIsAbsolute, setBadgeAbsolute] = useState(false);
    const inscreenTrackingUrls = Tracking.getInscreenUrl(ad);
    const trackingUrlClick = Tracking.getClickUrl(ad);

    const domRef = useRef<HTMLDivElement>(null);
    const adBadgeRef = useRef<HTMLDivElement>(null);
    const heartRef = useRef<HTMLDivElement>(null);

    const isFrontpagePersonal = product === 'frontpage-personal';
    const isNotPopular = product !== 'frontpage-popular';

    useTrackInscreen({
        adId: ad.itemId,
        tracking,
        ref: domRef,
        inscreenTrackingUrls,
        pulseContext: ad?.tracking?.pulse,
    });

    const ImageComponent = ad.image?.isAbsolute
        ? selectImageComp('EXTERNAL')
        : selectImageComp(ad.image?.type);

    const [hide, setHide] = useState(false);

    const updateUI = useCallback(() => {
        setHide(true);
    }, []);

    let multiColumn = false;
    if (ad?.overlay?.minColumns === 2) {
        multiColumn = true;
    }

    useEffect(() => {
        if (!adBadgeRef.current || !domRef.current || !heartRef.current) return;
        const itemW = domRef.current.offsetWidth - 10;
        const badgeWidth = (adBadgeRef.current.offsetWidth / itemW) * 100;
        const heartWidth = (heartRef.current.offsetWidth / itemW) * 100;
        setBadgeAbsolute(badgeWidth + heartWidth < 85);
    }, [adBadgeRef, domRef, heartRef]);

    return (
        <Ad
            key={ad.itemId}
            ref={domRef}
            onClick={(e) => {
                if (isFavouriteClicked(e, heartRef)) {
                    return;
                } else {
                    trackAdClick(ad.itemId, trackingUrlClick);
                    tracking.trackClickWithPulse(ad.tracking.pulse);
                }
            }}
            data-rec-item
            className={classNames(
                { 'overflowmenu-active': useOverflow },
                { hidden: hide },
                { 'enable-large-position': multiColumn },
                {
                    'recommendation-travel-crop-external-image':
                        ad.type === 'travel-hotel',
                }
            )}
        >
            <AdImage>
                <AdImageFrame>
                    <ImageComponent
                        alt=""
                        cdnUrl={imagesCdnUrl}
                        height={ad.image?.height}
                        sizes="(min-width: 990px) 330px, (min-width: 768px) 33vw, (min-width: 480px) 50vw"
                        url={ad.image?.url}
                        width={ad.image?.width}
                        isAbsolute={ad.image?.isAbsolute}
                        crop={
                            ad.type === 'travel-hotel' || ad.type === 'car-sub'
                                ? 'horizontal'
                                : 'vertical'
                        }
                    />
                    {ad.label && ad.image?.type !== 'LOGO' && (
                        <AdImageFrameOverlay>{ad.label}</AdImageFrameOverlay>
                    )}
                </AdImageFrame>
            </AdImage>
            <AdContent>
                <AdContentTitle lineClamp2>
                    <AdLink href={ad.url} title={ad.heading}>
                        {ad.heading}
                    </AdLink>
                </AdContentTitle>
                {ad.overlay?.p4p?.label && (
                    <AdBadge
                        ref={adBadgeRef}
                        className={
                            badgeIsAbsolute
                                ? 'recommendation-badge-absolute'
                                : 'recommendation-badge-static'
                        }
                    >
                        {ad.overlay.p4p.label}
                    </AdBadge>
                )}
                <AdContentDetails className="mb-8">
                    {ad.location?.combined ? (
                        <div>{ad.location.combined}</div>
                    ) : (
                        // Needed for position of overflow menu on items without location
                        <span className="hidden py-8"></span>
                    )}
                    {useOverflow && isNotPopular && (
                        <Overflowmenu
                            showHistoryLink={isFrontpagePersonal}
                            callback={updateUI}
                            blockItem={ad.tracking?.action?.block}
                            tracking={tracking}
                            url={url}
                        />
                    )}
                </AdContentDetails>
                <div className="reacommendation-secondary-content-wrapper">
                    {logo && (
                        <AdLogo className="block mr-8">
                            <img
                                src={logo}
                                alt=""
                                height={40}
                                style={{ height: '40px' }}
                            />
                        </AdLogo>
                    )}
                    <div>
                        {ad?.content?.company_name && (
                            <div className="block text-12 mb-4">
                                {ad.content.company_name}
                            </div>
                        )}
                        {ad.customLabel && (
                            <div className="block text-12">
                                {ad.customLabel}
                            </div>
                        )}
                    </div>
                </div>
            </AdContent>
            <ShippingOptionLabel recommendation={ad} className="absolute top-8 left-8" />
            {rightActions && <AdTools ref={heartRef}>{rightActions}</AdTools>}
        </Ad>
    );
};

export default StandardAd;
